import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { selectRecordingState, useHMSActions, useHMSStore } from '@100mslive/react-sdk';
import { isMobileOrTabletDevice, generateUrl } from '../utils/helper';
import PracticeMobile from '../components/Pages/Practice/PracticeMobile';
import PracticeDesktop from '../components/Pages/Practice/PracticeDesktop';
import VideoStream from '../components/Pages/components/portal/VideoStream';
import { getCodeSubmissionOutput } from '../redux/reducer/interviewReducer';
import { candidateCallEnd, submitCodingQuestion, resetCodeSubmissionOutput } from '../redux/action';
import { ToastManager } from '../components/Toast/ToastManager';
import { getDecodedOutput } from '../common/utils';
import { ConferenceMainView } from './mainView';
import { RTMP_RECORD_DEFAULT_RESOLUTION } from '../common/constants';
import { useConnectionState } from '@livekit/components-react';
export const MainViewConnector = ({
    form,
    timer,
    candidatePeer,
    callEnd,
    finalCallEnd,
    socket,
    codeEditorForm,
    setCodeEditorForm,
    changeInCode,
    setChangeInCode,
    onlineStatus,
    updateSocket,
    displayInput,
    setDisplayInput,
    setLivKitRoomState,
    microsoftKeys = [],
}) => {
    const dispatch = useDispatch();
    const hmsActions = useHMSActions();
    const roomLiveKitState = useConnectionState();
    const { roomId, role } = useParams();
    const codeOutput = useSelector(getCodeSubmissionOutput);
    const recordingState = useHMSStore(selectRecordingState);
    const [resolution, setResolution] = useState(RTMP_RECORD_DEFAULT_RESOLUTION);
    const [isQuestionTypeCoding, setIsQuestionTypeCoding] = useState(false);
    const [answerDuration, setAnswerDuration] = useState(0);
    const [recordingTriggered, setRecordingTriggered] = useState(false);
    const [questionInfo, setQuestionInfo] = useState({
        question: '',
        timeToAnswer: 0,
        questionNumber: form?.isResume ? (form?.codingFeedbackQuestions?.length || 0) + 1 : 1,
        actualQuestion: '',
        questionType: '',
    });
    const confirmLeave = useRef(false);
    const logSuccess = (message) => console.log(`✅ Success: ${message}`);
    const logError = (message, error) => console.error(`❌ Error: ${message}`, error);

    useEffect(() => {
        if (socket && roomId) {
            const intervalId = setInterval(() => {
                const timestamp = new Date().toISOString();
                socket.emit('mock-interview-active', { roomId, timestamp });
            }, 30000);
            return () => clearInterval(intervalId);
        }
    }, [socket, roomId]);

    useEffect(() => {
        if (roomLiveKitState) setLivKitRoomState(roomLiveKitState);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roomLiveKitState]);

    useEffect(() => {
        if (finalCallEnd) handleEndCall(true, 'Network Disconnected');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [finalCallEnd]);

    useEffect(() => {
        const handleRecordingSubmission = async () => {
            if (!recordingTriggered && !recordingState?.browser?.running) {
                await handleRecordingOn();
                setRecordingTriggered(true);
                logSuccess('Recording triggered and started successfully');
            }
        };
        handleRecordingSubmission();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleRecordingOn = async () => {
        try {
            await hmsActions.startRTMPOrRecording({
                meetingURL: `${generateUrl('meeting')}/room/preview/beam/${roomId}?skip_preview=true`,
                resolution: setResolution(resolution),
                record: true,
            });
            logSuccess('Started RTMP or recording successfully.');
        } catch (error) {
            logError('Error starting RTMP or recording', error);
        }
    };

    const handleRecordingOff = async () => {
        try {
            await hmsActions.stopRTMPAndRecording();
            logSuccess('Stopped RTMP and recording successfully.');
        } catch (error) {
            logError('Error stopping RTMP and recording', error);
        }
    };

    const stopMediaStreams = async () => {
        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const cameras = devices.filter((device) => device.kind === 'videoinput');
            for (const camera of cameras) {
                const stream = await navigator.mediaDevices.getUserMedia({ video: { deviceId: camera.deviceId } });
                stream.getTracks().forEach((track) => track.stop());
            }
            logSuccess('All camera streams stopped successfully');
        } catch (error) {
            logError('Failed to stop media streams', error);
        }
    };

    const saveCodingQuestion = ({ data }) => {
        try {
            logSuccess('Attempting to save coding question');
            const result = data ?? codeOutput;
            dispatch(
                submitCodingQuestion({
                    data: {
                        memory: result?.memory || 0,
                        runTime: result?.time || 0,
                        finalResult: getDecodedOutput(result) || '',
                        programmingLanguage: result?.language?.name || codeEditorForm?.programmingLanguage,
                        interviewId: form?.interviewId,
                        question: questionInfo?.actualQuestion,
                        timeTaken:
                            questionInfo?.timeToAnswer > answerDuration ? answerDuration : questionInfo?.timeToAnswer,
                        questionType: questionInfo?.questionType,
                        languageCode: codeEditorForm?.language || '',
                        code: codeEditorForm?.codeEditor || '',
                        prebuilt: codeEditorForm?.prebuilt || '',
                        questionLevel: codeEditorForm?.difficultyLevel,
                        questionId: codeEditorForm?.questionId,
                    },
                })
            );
            dispatch(resetCodeSubmissionOutput());
            if (changeInCode) setChangeInCode(false);
            logSuccess('Coding question saved successfully');
        } catch (error) {
            logError('Failed to save coding question', error);
        }
    };

    const handleEndCall = async (isNetworkDisconnect = false, endCallStatus) => {
        try {
            logSuccess('Starting to end the call process');
            if (isQuestionTypeCoding) {
                logSuccess('Saving coding question data');
                saveCodingQuestion({ data: codeOutput });
            }
            confirmLeave.current = true;

            logSuccess('Stopping camera streams');
            stopMediaStreams();

            if (recordingState?.browser?.running) {
                logSuccess('Stopping RTMP/browser recording');
                handleRecordingOff();
            }
            const doFinally = () => {
                logSuccess('Leaving the room');
                hmsActions.leave();
                ToastManager.clearAllToast();
                window.location.href = `/room/leave/${role}/${roomId}`;
            };
            if (onlineStatus) {
                if (isNetworkDisconnect) {
                    // A transient network blip cleared - rejoin via the preview screen
                    // instead of ending the interview, so a Wi-Fi hiccup doesn't
                    // silently orphan the session server-side with no report.
                    hmsActions.leave();
                    window.location.href = `/room/preview/${role}/${roomId}`;
                } else {
                    logSuccess(`Posting interview status: ${endCallStatus}`);
                    dispatch(
                        candidateCallEnd({
                            roomId,
                            endCallStatus,
                            doFinally,
                        })
                    );
                }
            }
        } catch (error) {
            console.error('Error in handleEndCall:', error);
            logSuccess(error?.message || 'Call end issue');
        }
    };

    useEffect(() => {
        // Deliberately does NOT call candidateCallEnd on 'unload'/'popstate' - a tab
        // close, refresh, or back-navigation is not the candidate ending the
        // interview. The backend heartbeat + resume-window cron now own finalizing
        // a session that's genuinely abandoned, so the candidate can rejoin the
        // same link within the resume window instead of the session being
        // hard-terminated by an accidental close.
        const handleBeforeUnload = (e) => {
            if (!confirmLeave.current) {
                e.preventDefault();
                e.returnValue = '';
                logSuccess('Before unload triggered, preventing default action.');
                return 'Are you sure you want to leave? Your task may not be saved.';
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        logSuccess('Added event listener for beforeunload.');
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            logSuccess('Removed event listener for beforeunload.');
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [confirmLeave]);

    return (
        <>
            {isMobileOrTabletDevice() ? (
                <PracticeMobile
                    form={form}
                    handleEndCall={handleEndCall}
                    timer={timer}
                    practiceStatus={false}
                    updateSocket={updateSocket}
                />
            ) : isQuestionTypeCoding || callEnd ? (
                <ConferenceMainView
                    form={form}
                    questionInfo={questionInfo}
                    handleEndCall={handleEndCall}
                    updateSocket={updateSocket}
                    onlineStatus={onlineStatus}
                    setAnswerDuration={setAnswerDuration}
                    candidatePeer={candidatePeer}
                    setCodeEditorForm={setCodeEditorForm}
                    codeEditorForm={codeEditorForm}
                    isQuestionTypeCoding={isQuestionTypeCoding}
                    timer={timer}
                    changeInCode={changeInCode}
                    setChangeInCode={setChangeInCode}
                    saveCodingQuestion={saveCodingQuestion}
                    setIsQuestionTypeCoding={setIsQuestionTypeCoding}
                    codeOutput={codeOutput}
                    setQuestionInfo={setQuestionInfo}
                    callEnd={callEnd}
                    displayInput={displayInput}
                    setDisplayInput={setDisplayInput}
                    microsoftKeys={microsoftKeys}
                />
            ) : (
                <PracticeDesktop
                    form={form}
                    handleEndCall={handleEndCall}
                    timer={timer}
                    practiceStatus={false}
                    updateSocket={updateSocket}
                />
            )}
            <VideoStream interviewId={form?.interviewId} roomId={roomId} />
        </>
    );
};
